// Headline and warning read ONE derivation but apply different rules to it, deliberately:
// `gatewayResponse` and `response` hold machine content (measured 2026-09-03: all 2,129 rows
// with `gatewayResponse` are JSON dumps, 1,339 of 1,348 `response` rows are raw objects), which
// affiliates must never be shown — yet the return code often lives only inside it, as
// `"xStatusReason":"R02 - Account Closed"`. So the headline drops it and the haystack keeps it.
const asText = (value) => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  return /^[[{]/.test(trimmed) ? '' : trimmed
}

const asHaystack = (value) => {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return ''
    }
  }
  return ''
}

export const describeGatewayResponse = (transactionData) => {
  const reasonText = asText(transactionData?.reasonText)
  const reasonCode = asText(transactionData?.reasonCode)
  const description = asText(transactionData?.description)

  const headline = [reasonText, reasonCode].filter(Boolean).join(' - ')
  const detail = description === headline ? '' : description

  const haystack = [
    transactionData?.reasonText,
    transactionData?.reasonCode,
    transactionData?.description,
    transactionData?.gatewayResponse,
    transactionData?.response,
  ].map(asHaystack).filter(Boolean).join(' ')

  return { headline: headline || '-', detail, haystack }
}

export const formatGatewayResponse = (transactionData) => {
  const { headline, detail } = describeGatewayResponse(transactionData)
  return { headline, detail }
}

// Display only — the charge path enforces the real refusal (services/payments/debitBlock.js).
// A colour is a prompt, not a control, so this list may be wider than the one that blocks.
const NEVER_RETRY = /\bR0(2|3|4|7)\b|\bR1(0|6)\b|\bR20\b|account closed|closed account|revoked|not authoriz|unauthoris|unauthoriz|frozen/i

export const isNeverRetry = (transactionData) => {
  if (!transactionData) return false
  return NEVER_RETRY.test(describeGatewayResponse(transactionData).haystack)
}

// WS 2074858. When Reschedule is not offered: what the server refuses (services/payments/
// reschedule.js — an unauthorized or administrative return, or an R08/R16/R20), plus the wider
// "Do not retry" text above, so the button never shows for a payment the server would refuse.
const NO_RESCHEDULE_BUCKETS = ['unauthorized', 'administrative']
const NO_RESCHEDULE_CODES = ['R08', 'R16', 'R20']

export const isRescheduleBlocked = (transactionData) => isNeverRetry(transactionData)
  || NO_RESCHEDULE_BUCKETS.includes(transactionData?.returnBucket)
  || NO_RESCHEDULE_CODES.includes(transactionData?.rCode)

export const formatReviewReason = (reviewReason) => {
  if (!reviewReason || typeof reviewReason !== 'string') return null
  const words = reviewReason
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!words.length) return null
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

export const isParkedCharge = (transactionData, statusValue) => statusValue === 'processing' && transactionData?.needsReview === true

export const describeParkedCharge = (transactionData) => {
  const headline = asText(transactionData?.reviewDetail)
    || formatReviewReason(transactionData?.reviewReason)
    || 'Needs review'
  const gateway = describeGatewayResponse(transactionData)
  const detail = [gateway.headline === '-' ? '' : gateway.headline, gateway.detail]
    .filter(Boolean)
    .join(' · ')
  return { headline, detail }
}
